interface SanitizeNonNegativeDecimalInputOptions {
  maxDigits?: number;
}

/** Normalize decimal input to digits plus a single "." separator. */
export const sanitizeNonNegativeDecimalInput = (
  value: string,
  options?: SanitizeNonNegativeDecimalInputOptions,
): string => {
  const normalizedValue = value.replace(",", ".").replace(/[^0-9.]/g, "");
  if (normalizedValue.length === 0) return "";

  const [rawIntegerPart = "", ...rawDecimalParts] = normalizedValue.split(".");
  const rawDecimalPart = rawDecimalParts.join("");
  const mergedDigits = `${rawIntegerPart}${rawDecimalPart}`;
  const maxDigits = options?.maxDigits;
  const hasValidMaxDigits =
    maxDigits != null && Number.isFinite(maxDigits) && maxDigits >= 0;
  const limitedDigits = hasValidMaxDigits
    ? mergedDigits.slice(0, Math.floor(maxDigits))
    : mergedDigits;
  if (limitedDigits.length === 0) return "";

  const integerLength = Math.min(rawIntegerPart.length, limitedDigits.length);
  const integerPart = limitedDigits.slice(0, integerLength);
  const hasDecimalPart = rawDecimalPart.length > 0 || normalizedValue.includes(".");
  const decimalPart = limitedDigits.slice(integerLength);

  if (hasDecimalPart) {
    return `${integerPart}.${decimalPart}`;
  }

  return integerPart;
};

/** Coerce decimal input into a valid non-negative decimal string. */
export const coerceToNonNegativeDecimalString = (
  value: string,
): string => {
  const sanitizedValue = sanitizeNonNegativeDecimalInput(value);
  if (sanitizedValue.length === 0 || sanitizedValue === ".") return "0";

  const valueWithoutTrailingSeparator = sanitizedValue.endsWith(".")
    ? sanitizedValue.slice(0, -1)
    : sanitizedValue;
  const withLeadingZero = valueWithoutTrailingSeparator.startsWith(".")
    ? `0${valueWithoutTrailingSeparator}`
    : valueWithoutTrailingSeparator;

  if (!/^\d+(\.\d+)?$/.test(withLeadingZero)) return "0";
  return withLeadingZero;
};

/** Parse a non-negative decimal input string and normalize invalid values to 0. */
export const parseNonNegativeDecimalInput = (
  value: string,
): number => {
  const normalizedValue = coerceToNonNegativeDecimalString(value);
  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return 0;
  return parsedValue;
};

/** Normalize a numeric value into a valid non-negative decimal input string. */
export const normalizeNonNegativeDecimalNumber = (
  value: number | null | undefined,
): string => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return "0";
  return sanitizeNonNegativeDecimalInput(String(parsedValue));
};
